/**
 * The (thin) browser glue for crash detection.
 *
 * Listens for the two most common crash sources — uncaught errors
 * (`window.onerror`) and unhandled promise rejections (`unhandledrejection`) —
 * then runs the pure helpers (stack parse, breadcrumbs, dedup) and hands a
 * finished report to `send`.
 *
 * Everything here is wrapped so error capture can never throw back into the
 * host app. The interesting logic lives in the pure modules; this file is kept
 * deliberately small and is exercised by the Playwright browser tests.
 */

import { BreadcrumbBuffer } from './breadcrumbs';
import { ErrorDeduper } from './dedup';
import { ErrorFilterOptions, getDropReason } from './filters';
import {
    buildErrorReport,
    dedupeKey,
    describeError,
    ErrorMechanism,
    ErrorReport,
    NetworkState,
    RequestContext,
} from './error-payload';

/** Read connectivity at error time (online + connection type when available). */
function readNetworkState(): NetworkState | undefined {
    if (typeof navigator === 'undefined') {
        return undefined;
    }
    const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
    return {
        online: typeof navigator.onLine === 'boolean' ? navigator.onLine : true,
        effectiveType: conn && typeof conn.effectiveType === 'string' ? conn.effectiveType : null,
    };
}

export interface ErrorCaptureContext {
    sessionId: string;
    endUserId: string | null;
    url: string;
    release?: string | null;
    environment?: string | null;
    commitSha?: string | null;
    dist?: string | null;
    sessionStartTimestampMs?: number;
    requestContext?: RequestContext;
    automaticProperties?: Record<string, unknown>;
    userProperties?: Record<string, unknown>;
    sessionProperties?: Record<string, unknown>;
}

export interface ErrorCaptureOptions {
    send: (report: ErrorReport) => void;
    getContext: () => ErrorCaptureContext;
    breadcrumbs: BreadcrumbBuffer;
    /** Host-configurable ignoreErrors / denyUrls / allowUrls (Sentry semantics). */
    filters?: ErrorFilterOptions;
    /**
     * Capture failed loads of third-party resources (other origins' scripts,
     * images, ads, trackers). Off by default: adblockers make cross-origin
     * resource failures overwhelmingly noise the app author cannot fix, so
     * only first-party (same-origin) resource failures are reported.
     */
    captureThirdPartyResourceErrors?: boolean;
}

/** Same-origin check for resource URLs; relative URLs count as first-party. */
function isFirstPartyUrl(url: string): boolean {
    try {
        if (typeof location === 'undefined') {
            return true;
        }
        return new URL(url, location.href).origin === location.origin;
    } catch {
        return true;
    }
}

export class ErrorCapture {
    private readonly opts: ErrorCaptureOptions;
    private readonly deduper: ErrorDeduper;
    private installed = false;
    private onError?: (event: ErrorEvent) => void;
    private onRejection?: (event: PromiseRejectionEvent) => void;
    private onResourceError?: (event: Event) => void;
    private onCsp?: (event: SecurityPolicyViolationEvent) => void;

    constructor(opts: ErrorCaptureOptions) {
        this.opts = opts;
        this.deduper = new ErrorDeduper();
    }

    install(): void {
        if (this.installed || typeof window === 'undefined') {
            return;
        }
        this.installed = true;

        this.onError = (event: ErrorEvent) => {
            // Prefer the real Error object; fall back to the message string for
            // browsers/cases that don't populate `event.error`.
            const thrown = event && event.error != null ? event.error : (event && event.message) || 'Unknown error';
            this.capture(thrown, 'onerror', false);
        };
        window.addEventListener('error', this.onError);

        this.onRejection = (event: PromiseRejectionEvent) => {
            const reason = event && 'reason' in event ? event.reason : event;
            this.capture(reason, 'onunhandledrejection', false);
        };
        window.addEventListener('unhandledrejection', this.onRejection);

        // Resource-load failures (404 img/script/link, failed JS chunk loads)
        // don't bubble, so they only surface to a CAPTURE-phase listener on
        // window. Script errors (target === window) are left to the bubble-phase
        // `onError` above to avoid double-reporting.
        this.onResourceError = (event: Event) => {
            const target = event.target as (HTMLElement & { src?: string; href?: string }) | null;
            if (!target || target === (window as unknown as HTMLElement) || !target.tagName) {
                return;
            }
            const tag = target.tagName.toLowerCase();
            const url = target.src || target.href || '';
            if (!url) {
                return;
            }
            if (!this.opts.captureThirdPartyResourceErrors && !isFirstPartyUrl(url)) {
                return;
            }
            this.capture(
                { name: 'ResourceLoadError', message: `Failed to load ${tag}: ${url}` },
                'resource',
                false,
            );
        };
        window.addEventListener('error', this.onResourceError, true);

        // Content-Security-Policy violations: cheap, high-signal security events.
        // Extension-injected scripts constantly trip host CSP; those are not the
        // app author's defect and drown the Issues feed.
        this.onCsp = (event: SecurityPolicyViolationEvent) => {
            const source = event.sourceFile || '';
            if (
                source.startsWith('chrome-extension:') ||
                source.startsWith('moz-extension:') ||
                source.startsWith('safari-extension:') ||
                source.startsWith('safari-web-extension:')
            ) {
                return;
            }
            const directive = event.effectiveDirective || event.violatedDirective || 'unknown';
            const blocked = event.blockedURI || 'inline';
            this.capture(
                { name: 'SecurityPolicyViolation', message: `Blocked ${blocked} (${directive})` },
                'csp',
                false,
            );
        };
        window.addEventListener('securitypolicyviolation', this.onCsp);
    }

    uninstall(): void {
        if (!this.installed || typeof window === 'undefined') {
            return;
        }
        if (this.onError) {
            window.removeEventListener('error', this.onError);
        }
        if (this.onRejection) {
            window.removeEventListener('unhandledrejection', this.onRejection);
        }
        if (this.onResourceError) {
            window.removeEventListener('error', this.onResourceError, true);
        }
        if (this.onCsp) {
            window.removeEventListener('securitypolicyviolation', this.onCsp);
        }
        this.installed = false;
    }

    /**
     * Build and send a report for a thrown value. Public so the manual
     * `captureException` path can reuse it later. `extra.componentStack` carries
     * the React component stack from the error boundary. Never throws.
     */
    capture(
        error: unknown,
        mechanism: ErrorMechanism,
        handled: boolean,
        extra?: { componentStack?: string },
    ): void {
        try {
            const described = describeError(error);
            // Cheap pre-build drop for ignored messages; URL filters need the
            // parsed stack and run again in getDropReason on the full report.
            if (
                getDropReason(
                    { exceptionType: described.type, value: described.value, stackFrames: [] },
                    this.opts.filters,
                )
            ) {
                return;
            }

            const ctx = this.opts.getContext();
            const report = buildErrorReport({
                error,
                mechanism,
                handled,
                sessionId: ctx.sessionId,
                endUserId: ctx.endUserId,
                url: ctx.url,
                release: ctx.release ?? null,
                environment: ctx.environment ?? null,
                commitSha: ctx.commitSha ?? null,
                dist: ctx.dist ?? null,
                componentStack: extra?.componentStack,
                networkState: readNetworkState(),
                requestContext: ctx.requestContext,
                sessionStartTimestampMs: ctx.sessionStartTimestampMs,
                breadcrumbs: this.opts.breadcrumbs.snapshot(),
                automaticProperties: ctx.automaticProperties,
                userProperties: ctx.userProperties,
                sessionProperties: ctx.sessionProperties,
            });

            if (getDropReason(report, this.opts.filters)) {
                return;
            }

            if (!this.deduper.shouldReport(dedupeKey(report))) {
                return;
            }

            this.opts.send(report);
        } catch {
            // Error capture must never break the host application.
        }
    }
}
